import React, { useEffect, useState } from 'react';
import API_URL from '../config';

const AUTH_KEY = 'admin_auth';

const mainColor = '#38b2ac';
const accentColor = '#48bb78';
const glassBg = 'rgba(255,255,255,0.55)';
const glassShadow = '0 8px 32px rgba(60, 60, 120, 0.18)';
const vibrantGradient = 'linear-gradient(120deg, #38b2ac 0%, #667eea 100%)';
const statsGlow = '0 0 16px 2px #38b2ac33, 0 2px 8px #48bb7833';
const statusColors = {
  pending: '#fbbf24', // amber
  completed: '#48bb78', // green
};

const statusIcons = {
  pending: '⏳',
  completed: '✅',
};

const fileIcons = {
  pdf: '📄',
  doc: '📝',
  docx: '📝',
  default: '📁',
};

function getFileIcon(filename) {
  if (!filename) return fileIcons.default;
  const ext = filename.split('.').pop().toLowerCase();
  return fileIcons[ext] || fileIcons.default;
}

const fontFamily = 'Inter, Segoe UI, Arial, sans-serif';

const AdminDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [auth, setAuth] = useState(localStorage.getItem(AUTH_KEY) || '');
  const [showLogin, setShowLogin] = useState(!auth);
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');
  const [modalImg, setModalImg] = useState(null);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [todayStats, setTodayStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [todayDate, setTodayDate] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(null);

  useEffect(() => {
    if (!auth) return;
    const fetchRequests = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/api/scan-requests`, {
          headers: { Authorization: auth },
        });
        if (res.status === 401) {
          setShowLogin(true);
          setAuth('');
          localStorage.removeItem(AUTH_KEY);
          setLoginError('Invalid credentials. Please log in again.');
          return;
        }
        if (!res.ok) throw new Error('Failed to fetch scan requests');
        const data = await res.json();
        setRequests(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/scan-requests/stats`, {
          headers: { Authorization: auth },
        });
        if (res.ok) {
          setStats(await res.json());
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    const fetchTodayStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/scan-requests/analytics/today`, {
          headers: { Authorization: auth },
        });
        if (res.ok) {
          setTodayStats(await res.json());
          setLastUpdated(new Date().toLocaleTimeString());
        }
      } catch {}
    };
    fetchRequests();
    fetchStats();
    fetchTodayStats();
    setTodayDate(new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }));
    const interval = setInterval(fetchTodayStats, 15000);
    return () => clearInterval(interval);
  }, [auth]);

  const getFileUrl = (file) => {
    if (!file || !file.url) {
      console.error('Invalid file object:', file);
      return '';
    }
    try {
      // If the URL is already a presigned S3 URL, return it
      if (file.url.includes('amazonaws.com')) {
        return file.url;
      }
      
      // If it's a local path, convert it to S3 URL
      if (file.url.startsWith('/uploads/')) {
        const filename = file.url.split('/').pop();
        return `${API_URL}/api/scan-requests/refresh-url?filename=${encodeURIComponent(filename)}`;
      }
      
      return file.url;
    } catch (error) {
      console.error('Error processing file URL:', error);
      return '';
    }
  };

  const handleDownload = async (file, filename) => {
    try {
      let url = getFileUrl(file);
      
      // If we need to get a fresh presigned URL
      if (!url || url.includes('/api/scan-requests/refresh-url')) {
        const response = await fetch(`${API_URL}/api/scan-requests/refresh-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
          },
          body: JSON.stringify({ filename: file.filename }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to refresh download URL');
        }
        
        const data = await response.json();
        if (!data.url) {
          throw new Error('No download URL received');
        }
        
        url = data.url;
        
        // Update the file URL in the state
        setRequests(prevRequests => 
          prevRequests.map(req => ({
            ...req,
            files: req.files.map(f => 
              f.filename === file.filename ? { ...f, url: data.url } : f
            ),
            proof: req.proof?.filename === file.filename ? { ...req.proof, url: data.url } : req.proof
          }))
        );
      }
      
      // Trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleImageError = (e) => {
    console.error('Error loading image:', e);
    e.target.style.display = 'none';
    if (e.target.parentElement) {
      e.target.parentElement.innerHTML += '<div style="color: red; font-size: 12px;">Error loading image</div>';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_KEY);
    setAuth('');
    setShowLogin(true);
    setLoginUser('');
    setLoginPass('');
    setLoginError('');
  };

  const filteredRequests = requests.filter(req =>
    req.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleLogin = (e) => {
    e.preventDefault();
    if (!loginUser || !loginPass) {
      setLoginError('Please enter username and password.');
      return;
    }
    const encoded = 'Basic ' + btoa(`${loginUser}:${loginPass}`);
    setAuth(encoded);
    localStorage.setItem(AUTH_KEY, encoded);
    setShowLogin(false);
    setLoginError('');
  };

  const closeModal = () => {
    console.log('Modal closed');
    setModalImg(null);
  };

  const handleStatusChange = async (id, newStatus) => {
    setStatusUpdating(id);
    try {
      // Update status
      await fetch(`${API_URL}/api/scan-requests/${id}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: auth,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      
      // Fetch all updated data in parallel
      const [requestsRes, statsRes, todayStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/scan-requests`, {
          headers: { Authorization: auth },
        }),
        fetch(`${API_URL}/api/scan-requests/stats`, {
          headers: { Authorization: auth },
        }),
        fetch(`${API_URL}/api/scan-requests/analytics/today`, {
          headers: { Authorization: auth },
        })
      ]);
      
      // Update requests list
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setRequests(data);
      }
      
      // Update stats
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
      // Update today's analytics
      if (todayStatsRes.ok) {
        setTodayStats(await todayStatsRes.json());
        setLastUpdated(new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
    setStatusUpdating(null);
  };

  if (showLogin) {
    return (
      <div style={{ minHeight: '100vh', background: vibrantGradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily }}>
        <form onSubmit={handleLogin} style={{ background: glassBg, padding: 40, borderRadius: 22, boxShadow: glassShadow, minWidth: 340, width: '100%', maxWidth: 380, backdropFilter: 'blur(16px)', border: '1.5px solid #e0e7ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
            <span style={{ fontSize: 36, color: mainColor, marginRight: 10, filter: 'drop-shadow(0 2px 8px #38b2ac44)' }}>🛡️</span>
            <h2 style={{ textAlign: 'center', fontWeight: 900, fontSize: '1.8rem', color: '#2d3748', margin: 0, letterSpacing: '-1px' }}>Admin Login</h2>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontWeight: 600, color: '#374151' }}>Username</label>
            <input
              type="text"
              value={loginUser}
              onChange={e => setLoginUser(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: 10, fontSize: '1.08rem', marginTop: 6, background: '#f9fafb', fontFamily }}
              autoFocus
            />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontWeight: 600, color: '#374151' }}>Password</label>
            <input
              type="password"
              value={loginPass}
              onChange={e => setLoginPass(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: 10, fontSize: '1.08rem', marginTop: 6, background: '#f9fafb', fontFamily }}
            />
          </div>
          {loginError && <div style={{ color: '#e53e3e', marginBottom: 16, textAlign: 'center', fontWeight: 600 }}>{loginError}</div>}
          <button type="submit" style={{ width: '100%', padding: '13px 0', background: vibrantGradient, color: 'white', border: 'none', borderRadius: 10, fontWeight: 900, fontSize: '1.13rem', cursor: 'pointer', marginTop: 8, boxShadow: '0 2px 8px #38b2ac22', letterSpacing: '0.5px', transition: 'background 0.2s' }}>Login</button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily, position: 'relative', overflowX: 'hidden' }}>
      {/* Loading Spinner Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            width: 64,
            height: 64,
            border: '7px solid #e0e7ef',
            borderTop: `7px solid ${mainColor}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      {/* Minimalist Header */}
      <header style={{ background: '#fff', borderBottom: '1.5px solid #e0e7ff', padding: '18px 0', boxShadow: '0 2px 12px #e0e7ff33', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px' }}>
          <span style={{ fontWeight: 900, fontSize: '1.5rem', color: mainColor, letterSpacing: '-1px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 28 }}>📝</span> Admin Dashboard
          </span>
          <button onClick={handleLogout} title="Logout" style={{ padding: '8px 22px', background: mainColor, color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 2px 8px #38b2ac22', letterSpacing: '0.5px', transition: 'background 0.2s' }}>
            Logout
          </button>
        </div>
      </header>
      {/* Today's Analytics Card - Enhanced */}
      <div style={{ maxWidth: 900, margin: '32px auto 0 auto', padding: '0 18px' }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 2px 12px #e0e7ff33',
          padding: 24,
          border: '1.5px solid #e0e7ff',
          marginBottom: 8,
          textAlign: 'center',
          position: 'relative'
        }}>
          <div style={{ fontWeight: 800, fontSize: 24, color: mainColor, marginBottom: 2 }}>Today</div>
          <div style={{ color: '#64748b', fontSize: 15, marginBottom: 8 }}>{todayDate} &middot; <span title="Auto-updates every 15 seconds">🔄</span></div>
          <div style={{ fontSize: 13, color: '#a0aec0', marginBottom: 12 }}>Transactions submitted today</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 36, marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 15, color: '#64748b', fontWeight: 600, marginBottom: 2 }}>Total</div>
              <div style={{ fontWeight: 900, fontSize: 22, color: '#2d3748', transition: 'color 0.2s' }}>📊 {todayStats.total}</div>
            </div>
            <div>
              <div style={{ fontSize: 15, color: '#64748b', fontWeight: 600, marginBottom: 2 }}>Completed</div>
              <div style={{ fontWeight: 900, fontSize: 22, color: accentColor, transition: 'color 0.2s' }}>✅ {todayStats.completed}</div>
            </div>
            <div>
              <div style={{ fontSize: 15, color: '#64748b', fontWeight: 600, marginBottom: 2 }}>Pending</div>
              <div style={{ fontWeight: 900, fontSize: 22, color: statusColors.pending, transition: 'color 0.2s' }}>⏳ {todayStats.pending}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 8 }}>Last updated: {lastUpdated}</div>
        </div>
      </div>
      {/* Search Bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18, maxWidth: 900, margin: '0 auto', padding: '0 18px' }}>
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '12px 18px',
            width: '100%',
            maxWidth: 320,
            borderRadius: 12,
            border: '1.5px solid #e0e7ff',
            fontSize: '1.05rem',
            background: '#fff',
            boxShadow: '0 1px 4px #e0e7ff22',
            fontWeight: 500,
            outline: 'none',
            color: '#374151',
            transition: 'box-shadow 0.2s',
          }}
        />
      </div>
      {/* Minimalist Table */}
      <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 12px #e0e7ff33', padding: 0, margin: '24px auto 38px auto', overflow: 'hidden', border: '1.5px solid #e0e7ff', maxWidth: 900 }}>
        <div style={{ overflowX: 'auto', borderRadius: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem', minWidth: 700, borderRadius: 18, fontFamily }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ border: 'none', padding: 14, fontWeight: 800, fontSize: 15, color: mainColor, minWidth: 120, textAlign: 'left' }}>Email</th>
                <th style={{ border: 'none', padding: 14, fontWeight: 800, fontSize: 15, color: mainColor, minWidth: 120, textAlign: 'left' }}>Files</th>
                <th style={{ border: 'none', padding: 14, fontWeight: 800, fontSize: 15, color: mainColor, minWidth: 120, textAlign: 'left' }}>Proof</th>
                <th style={{ border: 'none', padding: 14, fontWeight: 800, fontSize: 15, color: mainColor, minWidth: 90, textAlign: 'left' }}>Date</th>
                <th style={{ border: 'none', padding: 14, fontWeight: 800, fontSize: 15, color: mainColor, minWidth: 90, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((req, idx) => (
                <tr key={req._id} style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s', borderRadius: 12, boxShadow: 'none', cursor: 'pointer' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseOut={e => e.currentTarget.style.background = '#fff'}
                >
                  {/* Email */}
                  <td style={{ border: 'none', padding: 14, verticalAlign: 'top', fontWeight: 700, color: '#2d3748', fontSize: 14, wordBreak: 'break-all' }}>{req.email}</td>
                  {/* Files */}
                  <td style={{ border: 'none', padding: 14, verticalAlign: 'top' }}>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {req.files.map((file, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 15 }}>{getFileIcon(file.filename)}</span>
                          <span style={{ fontWeight: 700, color: '#374151', fontSize: 12, wordBreak: 'break-all' }}>{file.filename}</span>
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownload(file, file.filename);
                            }}
                            style={{ marginLeft: 4, color: mainColor, fontWeight: 800, textDecoration: 'underline', fontSize: 11, borderRadius: 6, padding: '2px 8px', background: '#e6fffa', transition: 'background 0.2s', boxShadow: '0 1px 4px #38b2ac11', cursor: 'pointer' }}
                            title="Download file"
                          >
                            [DL]
                          </a>
                        </li>
                      ))}
                    </ul>
                  </td>
                  {/* Proof of Payment */}
                  <td style={{ border: 'none', padding: 14, verticalAlign: 'top' }}>
                    {req.proof && (
                      <div style={{ background: '#f8fafc', border: `1.5px solid ${mainColor}22`, borderRadius: 12, boxShadow: 'none', padding: 7, minWidth: 80, maxWidth: 120, backdropFilter: 'blur(4px)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 15, color: mainColor }}>💸</span>
                          {req.proof.filename && (
                            <>
                              {req.proof.filename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                                <button
                                  style={{ marginLeft: 4, color: mainColor, fontWeight: 800, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', borderRadius: 6, padding: '2px 8px', backgroundColor: '#e6fffa', transition: 'background 0.2s', boxShadow: '0 1px 4px #38b2ac11' }}
                                  onClick={() => {
                                    const url = getFileUrl(req.proof);
                                    setModalImg(url);
                                  }}
                                  title="View image"
                                >
                                  [View]
                                </button>
                              ) : req.proof.filename.match(/\.(pdf)$/i) ? (
                                <a
                                  href={getFileUrl(req.proof)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ marginLeft: 4, color: mainColor, fontWeight: 800, textDecoration: 'underline', fontSize: 11, borderRadius: 6, padding: '2px 8px', background: '#e6fffa', transition: 'background 0.2s', boxShadow: '0 1px 4px #38b2ac11' }}
                                  title="View PDF"
                                >
                                  [View]
                                </a>
                              ) : null}
                            </>
                          )}
                        </div>
                        {req.proof.filename && (
                          <>
                            {req.proof.filename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                              <img 
                                src={getFileUrl(req.proof)} 
                                alt="Proof" 
                                style={{ maxWidth: 60, maxHeight: 44, borderRadius: 6, border: `1px solid ${mainColor}22`, marginTop: 3 }} 
                                onError={handleImageError}
                              />
                            ) : req.proof.filename.match(/\.(pdf)$/i) ? (
                              <iframe src={getFileUrl(req.proof)} title="Proof PDF" style={{ width: 60, height: 44, border: `1px solid ${mainColor}22`, borderRadius: 6, marginTop: 3 }}></iframe>
                            ) : null}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Date */}
                  <td style={{ border: 'none', padding: 14, verticalAlign: 'top', fontSize: 12, color: '#64748b', fontWeight: 500 }}>{new Date(req.createdAt).toLocaleString()}</td>
                  {/* Status Dropdown */}
                  <td style={{ border: 'none', padding: 14, textAlign: 'center', verticalAlign: 'top' }}>
                    <select
                      value={req.status}
                      onChange={e => handleStatusChange(req._id, e.target.value)}
                      disabled={statusUpdating === req._id}
                      style={{
                        padding: '6px 18px',
                        borderRadius: 18,
                        fontWeight: 700,
                        color: '#fff',
                        background: statusColors[req.status] || '#a0aec0',
                        fontSize: '1rem',
                        minWidth: 80,
                        border: 'none',
                        outline: 'none',
                        cursor: statusUpdating === req._id ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s',
                        textAlign: 'center',
                        appearance: 'none',
                        fontFamily,
                      }}
                      title="Change status"
                    >
                      <option value="pending">⏳ Pending</option>
                      <option value="completed">✅ Completed</option>
                    </select>
                    {statusUpdating === req._id && (
                      <span style={{ display: 'inline-block', width: 16, height: 16 }}>
                        <svg viewBox="0 0 50 50" style={{ width: 16, height: 16 }}>
                          <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
                            <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
                          </circle>
                        </svg>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredRequests.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: '#a0aec0', fontWeight: 700, marginTop: 24, fontSize: 18 }}>
              No scan requests found.
            </div>
          )}
        </div>
      </div>
      {/* Modal Glassmorphism */}
      {modalImg && (
        <div onClick={closeModal} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.3s' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: glassBg, borderRadius: 18, padding: 22, boxShadow: glassShadow, maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', backdropFilter: 'blur(18px)', border: `2.5px solid ${mainColor}33`, animation: 'fadeIn 0.4s' }}>
            <img src={modalImg} alt="Proof" style={{ maxWidth: '80vw', maxHeight: '70vh', borderRadius: 12, marginBottom: 18, boxShadow: '0 2px 12px #38b2ac22' }} />
            <button onClick={closeModal} style={{ padding: '10px 32px', background: vibrantGradient, color: 'white', border: 'none', borderRadius: 10, fontWeight: 900, fontSize: '1.08rem', cursor: 'pointer', boxShadow: '0 2px 8px #38b2ac22', letterSpacing: '0.5px', transition: 'background 0.2s' }}>Close</button>
          </div>
        </div>
      )}
      {/* FadeIn and IconPulse animation keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes iconPulse {
          from { filter: drop-shadow(0 2px 8px #38b2ac33); }
          to { filter: drop-shadow(0 6px 16px #48bb7833); }
        }
      `}</style>
      {/* Footer */}
      <footer style={{ color: '#a0aec0', fontSize: '1.08rem', marginTop: 32, textAlign: 'center', padding: '18px 0 10px 0', borderTop: '1.5px solid #e0e7ff', background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', boxShadow: '0 -2px 12px #38b2ac11', letterSpacing: '0.01em' }}>
        &copy; {new Date().getFullYear()} Student Document AI Service. All rights reserved.
      </footer>
    </div>
  );
};

export default AdminDashboard; 